
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useJournalReminders } from "@/hooks/useJournalReminders";
import { Bell, Calendar, Check, Clock, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface JournalRemindersProps {
  limit?: number;
}

// Define a type for our reminder to avoid confusion
interface JournalReminder {
  id: string;
  frequency: string;
  time_of_day: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  user_id: string;
}

export const JournalReminders = ({ limit }: JournalRemindersProps) => {
  const { reminders = [], isLoading, deleteReminder } = useJournalReminders();
  const [showAddReminder, setShowAddReminder] = useState(false);
  const { toast } = useToast();
  
  // Apply limit safely and handle potential undefined values
  const displayedReminders = Array.isArray(reminders) && reminders.length > 0 
    ? (limit ? reminders.slice(0, limit) : reminders) 
    : [];

  const handleMarkComplete = (id: string) => {
    if (deleteReminder) {
      deleteReminder(id);
      toast({
        title: "Rappel terminé",
        description: "Le rappel a été marqué comme terminé et supprimé",
      });
    }
  };
  
  return (
    <div className="space-y-4 animate-fade-in">
      <Card className="border shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-500" />
              Rappels
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowAddReminder(!showAddReminder)}
              className="h-8 gap-1"
            >
              <Plus className="h-3.5 w-3.5" /> Ajouter
            </Button>
          </div>
          <CardDescription>Vos rappels de santé personnels</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array(3).fill(0).map((_, i) => (
                <div key={i} className="h-16 bg-muted/40 rounded-md animate-pulse" />
              ))}
            </div>
          ) : displayedReminders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Aucun rappel pour le moment</p>
              <Button 
                variant="link" 
                className="mt-2"
                onClick={() => setShowAddReminder(true)}
              >
                Créer un rappel
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {displayedReminders.map((reminder: JournalReminder) => (
                <div 
                  key={reminder.id} 
                  className="p-3 border rounded-lg flex justify-between items-start gap-3 hover:bg-accent/50 transition-colors"
                >
                  <div className="flex gap-3 items-start flex-1 min-w-0">
                    <div className="bg-primary/10 p-2 rounded-full">
                      {reminder.frequency === 'daily' ? (
                        <Clock className="h-4 w-4 text-primary" />
                      ) : (
                        <Calendar className="h-4 w-4 text-primary" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">
                        Rappel {reminder.frequency === 'daily' ? 'quotidien' : 
                               reminder.frequency === 'weekly' ? 'hebdomadaire' : 
                               reminder.frequency === 'weekdays' ? 'jours ouvrables' : 
                               reminder.frequency === 'weekends' ? 'weekend' : 'personnalisé'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {reminder.time_of_day ? `À ${reminder.time_of_day.substring(0, 5)}` : 'Horaire non défini'}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          {reminder.created_at ? format(new Date(reminder.created_at), "dd/MM/yyyy") : 'Date inconnue'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                    onClick={() => handleMarkComplete(reminder.id)}
                  >
                    <Check className="h-4 w-4" />
                    <span className="sr-only">Marquer comme terminé</span>
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          {/* Reminder form */}
          {showAddReminder && (
            <div className="mt-4 p-3 border rounded-lg">
              {/* Reminder form will go here in the future */}
              <p className="text-center text-sm text-muted-foreground">
                Fonctionnalité à venir
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
